import { createLiquidGlassEngine } from "@/lib/design/liquid-glass-engine";
import type {
  LiquidGlassEffect,
  LiquidGlassEngine,
  LiquidGlassOptions,
} from "@/lib/design/liquid-glass-engine";

const SURFACE_SELECTOR =
  '[data-liquid-glass], [data-sonner-toast][data-styled="true"]';
const DEFAULT_MAX_ACTIVE = 12;
const DEFAULT_MAX_DIMENSION = 800;
const DEFAULT_MAX_DOCK_DIMENSION = 1000;

export type LiquidGlassPreset = "subtle" | "standard" | "elevated" | "dock";

export const LIQUID_GLASS_PRESETS = {
  subtle: {
    scale: -60,
    chroma: 3,
    border: 0.1,
    mapBlur: 14,
    blur: 5,
    saturate: 1.25,
    fallbackBlur: 20,
  },
  standard: {
    scale: -84,
    chroma: 4,
    border: 0.09,
    mapBlur: 14,
    blur: 4,
    saturate: 1.35,
    fallbackBlur: 24,
  },
  elevated: {
    scale: -112,
    chroma: 6,
    border: 0.08,
    mapBlur: 12,
    blur: 3,
    saturate: 1.45,
    fallbackBlur: 28,
  },
  dock: {
    scale: -92,
    chroma: 4,
    border: 0.06,
    mapBlur: 16,
    blur: 5,
    saturate: 1.4,
    fallbackBlur: 32,
  },
} as const satisfies Record<LiquidGlassPreset, LiquidGlassOptions>;

const PRESET_PRIORITY: Record<LiquidGlassPreset, number> = {
  subtle: 1,
  standard: 2,
  dock: 3,
  elevated: 4,
};

type FallbackReason =
  | "budget"
  | "forced-colors"
  | "oversized"
  | "paper"
  | "print"
  | "reduced-transparency"
  | "solid"
  | "unsupported";

interface SurfaceRecord {
  effect?: LiquidGlassEffect;
  element: HTMLElement;
  order: number;
  preset: LiquidGlassPreset;
  visible: boolean;
}

interface LiquidGlassControllerOptions {
  document?: Document;
  engine?: LiquidGlassEngine;
  IntersectionObserver?: typeof IntersectionObserver;
  ResizeObserver?: typeof ResizeObserver;
  MutationObserver?: typeof MutationObserver;
  matchMedia?: (query: string) => MediaQueryList;
  maxActive?: number;
  maxDimension?: number;
  maxDockDimension?: number;
}

export interface LiquidGlassController {
  readonly activeCount: number;
  destroy: () => void;
  refresh: (element?: HTMLElement) => void;
}

function isLiquidGlassSurface(node: Node): node is HTMLElement {
  return node instanceof HTMLElement && node.matches(SURFACE_SELECTOR);
}

function getPreset(element: HTMLElement): LiquidGlassPreset {
  if (element.matches('[data-sonner-toast][data-styled="true"]')) {
    return "elevated";
  }
  const candidate = element.dataset.liquidGlass;
  return candidate === "subtle" ||
    candidate === "elevated" ||
    candidate === "dock"
    ? candidate
    : "standard";
}

function surfacesWithin(node: Node): HTMLElement[] {
  const surfaces: HTMLElement[] = [];
  if (isLiquidGlassSurface(node)) surfaces.push(node);
  if (node instanceof Element) {
    surfaces.push(...node.querySelectorAll<HTMLElement>(SURFACE_SELECTOR));
  }
  return surfaces;
}

function createInertMediaQueryList(query: string): MediaQueryList {
  return {
    matches: false,
    media: query,
    onchange: null,
    addEventListener: () => {},
    addListener: () => {},
    dispatchEvent: () => true,
    removeEventListener: () => {},
    removeListener: () => {},
  };
}

export function createLiquidGlassController(
  options: LiquidGlassControllerOptions = {},
): LiquidGlassController {
  if (!options.document && typeof document === "undefined") {
    throw new Error("Liquid glass can only be initialized in a browser");
  }

  const browserDocument = options.document ?? document;
  const view = browserDocument.defaultView;
  const engine = options.engine ?? createLiquidGlassEngine();
  const Intersection =
    options.IntersectionObserver ?? view?.IntersectionObserver;
  const Resize = options.ResizeObserver ?? view?.ResizeObserver;
  const Mutations = options.MutationObserver ?? view?.MutationObserver;
  const media =
    options.matchMedia ??
    (view?.matchMedia
      ? view.matchMedia.bind(view)
      : (query: string) => createInertMediaQueryList(query));
  const maxActive = options.maxActive ?? DEFAULT_MAX_ACTIVE;
  const maxDimension = options.maxDimension ?? DEFAULT_MAX_DIMENSION;
  const maxDockDimension =
    options.maxDockDimension ?? DEFAULT_MAX_DOCK_DIMENSION;
  const records = new Map<HTMLElement, SurfaceRecord>();
  let sequence = 0;
  let destroyed = false;

  const reducedTransparency = media("(prefers-reduced-transparency: reduce)");
  const forcedColors = media("(forced-colors: active)");
  const print = media("print");
  const mediaQueries = [reducedTransparency, forcedColors, print];

  const activeCount = () => {
    let count = 0;
    for (const record of records.values()) {
      if (record.effect?.supported) count += 1;
    }
    return count;
  };

  const disabledReason = (): FallbackReason | undefined => {
    if (reducedTransparency.matches) return "reduced-transparency";
    if (forcedColors.matches) return "forced-colors";
    if (print.matches) return "print";
    return engine.supported ? undefined : "unsupported";
  };

  const clearRuntimeState = (element: HTMLElement) => {
    delete element.dataset.liquidGlassFallback;
    delete element.dataset.liquidGlassRuntime;
  };

  const destroyEffect = (record: SurfaceRecord) => {
    record.effect?.destroy();
    record.effect = undefined;
  };

  const markFallback = (record: SurfaceRecord, reason: FallbackReason) => {
    destroyEffect(record);
    record.element.dataset.liquidGlassRuntime = "fallback";
    record.element.dataset.liquidGlassFallback = reason;
  };

  const reconcile = (record: SurfaceRecord) => {
    const { element } = record;
    if (
      destroyed ||
      !element.isConnected ||
      !element.matches(SURFACE_SELECTOR)
    ) {
      return;
    }

    const globalFallback = disabledReason();
    if (globalFallback) {
      markFallback(record, globalFallback);
      return;
    }

    if (!record.visible) {
      destroyEffect(record);
      clearRuntimeState(element);
      element.dataset.liquidGlassRuntime = "idle";
      return;
    }

    if (element.dataset.liquidGlassDensity === "solid") {
      markFallback(record, "solid");
      return;
    }

    if (element.dataset.appearance === "paper") {
      markFallback(record, "paper");
      return;
    }

    const width = element.offsetWidth;
    const height = element.offsetHeight;
    const isTallNarrowDock =
      record.preset === "dock" &&
      Math.max(width, height) <= maxDockDimension &&
      width * height <= maxDimension * maxDimension;
    if ((width > maxDimension || height > maxDimension) && !isTallNarrowDock) {
      markFallback(record, "oversized");
      return;
    }

    if (width <= 0 || height <= 0) {
      destroyEffect(record);
      clearRuntimeState(element);
      element.dataset.liquidGlassRuntime = "idle";
      return;
    }

    if (record.effect) {
      record.effect.refresh();
      return;
    }

    if (activeCount() >= maxActive) {
      const incomingPriority = PRESET_PRIORITY[record.preset];
      const evictionCandidate = [...records.values()]
        .filter((candidate) => candidate.effect?.supported)
        .sort((left, right) => {
          const priorityDifference =
            PRESET_PRIORITY[left.preset] - PRESET_PRIORITY[right.preset];
          return priorityDifference || right.order - left.order;
        })[0];

      if (PRESET_PRIORITY[evictionCandidate.preset] >= incomingPriority) {
        markFallback(record, "budget");
        return;
      }

      markFallback(evictionCandidate, "budget");
    }

    clearRuntimeState(element);
    record.effect = engine.apply(element, LIQUID_GLASS_PRESETS[record.preset]);
    element.dataset.liquidGlassRuntime = record.effect.supported
      ? "active"
      : "fallback";
    if (!record.effect.supported) {
      element.dataset.liquidGlassFallback = "unsupported";
    }
  };

  const reconcileAll = () => {
    const ordered = [...records.values()].sort((a, b) => a.order - b.order);
    for (const record of ordered) reconcile(record);
  };

  const intersectionObserver = Intersection
    ? new Intersection((entries) => {
        for (const entry of entries) {
          const record = records.get(entry.target as HTMLElement);
          if (!record) continue;
          record.visible = entry.isIntersecting;
          reconcile(record);
        }
        reconcileAll();
      })
    : undefined;

  const resizeObserver = Resize
    ? new Resize((entries) => {
        for (const entry of entries) {
          const record = records.get(entry.target as HTMLElement);
          if (record) reconcile(record);
        }
        reconcileAll();
      })
    : undefined;

  const register = (element: HTMLElement) => {
    const existing = records.get(element);
    if (existing) {
      const preset = getPreset(element);
      if (existing.preset !== preset) {
        destroyEffect(existing);
        existing.preset = preset;
      }
      reconcile(existing);
      return;
    }

    const record: SurfaceRecord = {
      element,
      order: sequence++,
      preset: getPreset(element),
      visible: intersectionObserver === undefined,
    };
    records.set(element, record);
    intersectionObserver?.observe(element);
    resizeObserver?.observe(element);
    reconcile(record);
  };

  const unregister = (element: HTMLElement) => {
    const record = records.get(element);
    if (!record) return;
    destroyEffect(record);
    intersectionObserver?.unobserve(element);
    resizeObserver?.unobserve(element);
    clearRuntimeState(element);
    records.delete(element);
  };

  const mutationObserver = Mutations
    ? new Mutations((mutations) => {
        for (const mutation of mutations) {
          if (mutation.type === "attributes") {
            const target = mutation.target as HTMLElement;
            if (target.matches(SURFACE_SELECTOR)) register(target);
            else unregister(target);
            continue;
          }

          for (const removedNode of mutation.removedNodes) {
            for (const surface of surfacesWithin(removedNode))
              unregister(surface);
          }
          for (const addedNode of mutation.addedNodes) {
            for (const surface of surfacesWithin(addedNode)) register(surface);
          }
        }
        reconcileAll();
      })
    : undefined;

  const handleMediaChange = () => reconcileAll();
  for (const query of mediaQueries) {
    query.addEventListener("change", handleMediaChange);
  }

  for (const element of browserDocument.querySelectorAll<HTMLElement>(
    SURFACE_SELECTOR,
  )) {
    register(element);
  }

  mutationObserver?.observe(browserDocument.documentElement, {
    attributeFilter: [
      "data-appearance",
      "data-liquid-glass",
      "data-liquid-glass-density",
      "data-sonner-toast",
      "data-styled",
    ],
    attributes: true,
    childList: true,
    subtree: true,
  });

  return {
    get activeCount() {
      return activeCount();
    },
    refresh: (element) => {
      if (element) {
        const record = records.get(element);
        if (record) reconcile(record);
      } else {
        reconcileAll();
      }
    },
    destroy: () => {
      if (destroyed) return;
      destroyed = true;
      mutationObserver?.disconnect();
      intersectionObserver?.disconnect();
      resizeObserver?.disconnect();
      for (const query of mediaQueries) {
        query.removeEventListener("change", handleMediaChange);
      }
      for (const record of records.values()) {
        destroyEffect(record);
        clearRuntimeState(record.element);
      }
      records.clear();
      engine.destroy();
    },
  };
}
